import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  FormControlLabel, Switch, Stack, Typography, IconButton, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

export type DashboardWidgetKey =
  | 'summaryCards'
  | 'hourlyChart'
  | 'throughput'
  | 'pipeline'
  | 'activeWaves'
  | 'activityFeed';

export type DashboardWidgetVisibility = Record<DashboardWidgetKey, boolean>;

export const DEFAULT_WIDGET_VISIBILITY: DashboardWidgetVisibility = {
  summaryCards: true,
  hourlyChart: true,
  throughput: true,
  pipeline: true,
  activeWaves: true,
  activityFeed: true,
};

const STORAGE_KEY = 'wms.dashboard.widgetVisibility.v1';

export function loadWidgetVisibility(): DashboardWidgetVisibility {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_WIDGET_VISIBILITY };
    const parsed = JSON.parse(raw) as Partial<DashboardWidgetVisibility>;
    return { ...DEFAULT_WIDGET_VISIBILITY, ...parsed };
  } catch {
    return { ...DEFAULT_WIDGET_VISIBILITY };
  }
}

export function saveWidgetVisibility(value: DashboardWidgetVisibility): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* ignore quota / private mode */
  }
}

const WIDGET_META: { key: DashboardWidgetKey; labelKey: string }[] = [
  { key: 'summaryCards', labelKey: 'pages.dashboard.widgets.summaryCards' },
  { key: 'hourlyChart', labelKey: 'pages.dashboard.widgets.hourlyChart' },
  { key: 'throughput', labelKey: 'pages.dashboard.widgets.throughput' },
  { key: 'pipeline', labelKey: 'pages.dashboard.widgets.pipeline' },
  { key: 'activeWaves', labelKey: 'pages.dashboard.widgets.activeWaves' },
  { key: 'activityFeed', labelKey: 'pages.dashboard.widgets.activityFeed' },
];

interface Props {
  open: boolean;
  value: DashboardWidgetVisibility;
  onChange: (next: DashboardWidgetVisibility) => void;
  onClose: () => void;
}

const DashboardWidgetSettings: React.FC<Props> = ({ open, value, onChange, onClose }) => {
  const { t } = useTranslation('warehouse');

  const toggle = (key: DashboardWidgetKey) => {
    const next = { ...value, [key]: !value[key] };
    // En az bir widget açık kalsın
    if (!Object.values(next).some(Boolean)) return;
    onChange(next);
    saveWidgetVisibility(next);
  };

  const reset = () => {
    const next = { ...DEFAULT_WIDGET_VISIBILITY };
    onChange(next);
    saveWidgetVisibility(next);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        {t('pages.dashboard.widgetSettings')}
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('pages.dashboard.widgets.help')}
        </Typography>
        <Stack spacing={0.5}>
          {WIDGET_META.map((w, idx) => (
            <React.Fragment key={w.key}>
              {idx > 0 && <Divider flexItem />}
              <FormControlLabel
                control={
                  <Switch
                    checked={!!value[w.key]}
                    onChange={() => toggle(w.key)}
                    color="primary"
                  />
                }
                label={t(w.labelKey)}
                sx={{ mx: 0, py: 0.5, width: '100%', justifyContent: 'space-between', ml: 0 }}
                labelPlacement="start"
              />
            </React.Fragment>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={reset} color="inherit">
          {t('pages.dashboard.widgets.reset')}
        </Button>
        <Button onClick={onClose} variant="contained">
          {t('pages.dashboard.widgets.done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardWidgetSettings;
